import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  Boxes,
  AlertTriangle,
  Truck,
  MapPin,
  FileText,
  Plus,
  ArrowRight,
  Barcode,
  TrendingUp,
  History,
  ShoppingCart,
  Users,
  DollarSign,
  Facebook,
} from "lucide-react";
import { API } from "@/App";

export default function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/reports/dashboard-stats`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  const statCards = [
    {
      title: "Total Products",
      value: stats?.total_products || 0,
      icon: Package,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      title: "Total Units",
      value: stats?.total_units || 0,
      icon: Boxes,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
    },
    {
      title: "Low Stock Alerts",
      value: stats?.low_stock_count || 0,
      icon: AlertTriangle,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      alert: (stats?.low_stock_count || 0) > 0,
    },
    {
      title: "Sales Revenue",
      value: formatCurrency(stats?.total_sales_amount || 0),
      icon: DollarSign,
      color: "text-emerald-500",
      bgColor: "bg-emerald-500/10",
      isCurrency: true,
    },
    {
      title: "Active Sellers",
      value: stats?.sellers_count || 0,
      icon: Users,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      title: "Marketplace",
      value: stats?.active_listings || 0,
      icon: Facebook,
      color: "text-blue-600",
      bgColor: "bg-blue-600/10",
    },
  ];

  const quickActions = [
    {
      label: "New Sale",
      icon: ShoppingCart,
      path: "/sales",
      primary: true,
    },
    {
      label: "New Receiving",
      icon: Plus,
      path: "/receiving/new",
    },
    {
      label: "Add Product",
      icon: Package,
      path: "/products/new",
    },
    {
      label: "Marketplace",
      icon: Facebook,
      path: "/marketplace",
    },
  ];

  const formatAction = (action) => {
    const actions = {
      create: "Created",
      update: "Updated",
      delete: "Deleted",
      transfer: "Transferred",
      adjust: "Adjusted",
      complete: "Completed",
      cancel: "Cancelled",
      generate_barcode: "Generated barcode for",
      sale: "Sold",
      refund: "Refunded",
      payout: "Paid commission for",
      update_status: "Updated status of",
    };
    return actions[action] || action;
  };

  const formatEntityType = (type) => {
    return type.replace(/_/g, " ");
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Dashboard
            </h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.name?.split(" ")[0]}
            </p>
          </div>
          <Link to="/receiving/new">
            <Button className="rounded-sm" data-testid="new-receiving-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Receiving Session
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} className="stat-card rounded-sm border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-sm ${stat.bgColor} flex items-center justify-center`}>
                    <stat.icon className={`h-5 w-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">
                      {stat.title}
                    </p>
                    <p className="font-data text-2xl font-semibold">
                      {loading ? "-" : stat.isCurrency ? stat.value : stat.value.toLocaleString()}
                    </p>
                  </div>
                </div>
                {stat.alert && (
                  <Badge variant="destructive" className="mt-2 rounded-sm">
                    Needs attention
                  </Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="rounded-sm border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading font-semibold">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action, index) => (
                  <Link
                    key={index}
                    to={action.path}
                    className={`quick-action ${action.primary ? "bg-primary text-primary-foreground hover:bg-primary/90 border-primary" : ""}`}
                    data-testid={`quick-action-${action.path.replace("/", "")}`}
                  >
                    <action.icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{action.label}</span>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2 rounded-sm border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-heading font-semibold">
                Recent Activity
              </CardTitle>
              <Link to="/audit-logs">
                <Button variant="ghost" size="sm" className="text-xs">
                  View all <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 skeleton-pulse" />
                  ))}
                </div>
              ) : stats?.recent_activity?.length > 0 ? (
                <div className="space-y-1 divide-y divide-border">
                  {stats.recent_activity.map((log, index) => (
                    <div key={index} className="py-3 flex items-start gap-3">
                      <div className="h-8 w-8 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
                        <History className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-medium">{formatAction(log.action)}</span>{" "}
                          <span className="text-muted-foreground">{formatEntityType(log.entity_type)}</span>
                        </p>
                        <p className="text-xs text-muted-foreground font-data">
                          {new Date(log.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Low Stock Alert */}
        {stats?.low_stock_count > 0 && (
          <Card className="rounded-sm border border-orange-500/20 bg-orange-500/5">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-sm bg-orange-500/10 flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium">Low Stock Alert</p>
                    <p className="text-sm text-muted-foreground">
                      {stats.low_stock_count} product(s) below threshold
                    </p>
                  </div>
                </div>
                <Link to="/reports">
                  <Button variant="outline" size="sm" className="rounded-sm" data-testid="view-low-stock-btn">
                    View Details
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
